<template>
  <div>
    <UISelect
      id="add-field"
      v-model:value="selectedField"
      :options="fieldOptions"
      :placeholder="t('common.scoreEditor.addFieldPlaceholder')"
      filterable
      @update:value="addField"
    />

    <div
      ref="expressionContainer"
      class="my-3 flex min-h-[6rem] cursor-text flex-wrap gap-2 rounded border p-3 transition-all"
      :class="{
        'border-primary-300': cursorPosition >= 0,
        'border-gray-300': cursorPosition < 0,
      }"
      tabindex="0"
      @click="handleContainerClick"
      @keydown="handleKeydown"
    >
      <template v-for="(field, index) in fieldsWithLabels" :key="`field-${index}`">
        <!-- Cursor before this field -->
        <div v-if="cursorPosition === index" class="cursor-blink h-7 w-0.5 bg-primary-500"></div>

        <UITag
          :ref="`field-${index}`"
          class="cursor-pointer select-none rounded p-2.5 transition-all hover:ring-2 hover:ring-primary-200"
          :class="{
            'ring-2 ring-primary-500': cursorPosition === index + 1,
            'bg-primary-50': cursorPosition === index + 1,
            'opacity-75': cursorPosition === index,
          }"
          :type="field.length > 1 ? 'info' : 'default'"
          @click.stop="setCursorPosition(index + 1)"
        >
          {{ field }}
        </UITag>
      </template>

      <!-- Cursor at the end -->
      <div
        v-if="cursorPosition === fieldsWithLabels.length"
        class="cursor-blink h-7 w-0.5 bg-primary-500"
      ></div>
    </div>
    <div class="mb-2 grid grid-cols-4 gap-2">
      <UIButton
        v-for="(num, index) in numberPad"
        id="number-pad"
        :key="num"
        size="large"
        class="flex transform items-center justify-center rounded-[100px] px-8 text-lg font-bold transition-transform duration-100 active:scale-90"
        :class="{ 'col-span-2': index >= numberPad.length - 2 }"
        type="tertiary"
        :style="buttonStyle(num)"
        @click="addNumber(num)"
      >
        {{ num }}
      </UIButton>
    </div>
    <div class="my-2 mt-3 flex items-center text-sm">
      <span
        class="mr-2 inline-block h-2.5 w-2.5 rounded-full border border-black bg-primary-500"
      ></span>
      <span class="mr-4">{{ t("common.scoreEditor.fieldLabel") }}</span>
      <span class="mr-2 inline-block h-2.5 w-2.5 rounded-full border border-black bg-black"></span>
      <span class="mr-4">{{ t("common.scoreEditor.numberLabel") }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { isTrueCustomObjectField } from "@/util/customObjectUtils";
import { UIButton, UISelect, UITag } from "@gohighlevel/ghl-ui";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { PRODUCT_TYPE } from "../../../../form-survey/src/helper/const";

const { t } = useI18n();
const selectedField = ref(null);
const cursorPosition = ref(0);
const expressionContainer = ref<HTMLElement | null>(null);

const store = useAppStore();

const props = defineProps({
  calculation: {
    type: Object,
    default: null,
  },
  productType: {
    type: String,
    default: PRODUCT_TYPE.FORM,
  },
});

const emit = defineEmits(["update:calculation"]);

const includedTypes = ["monetory", "numerical", "score", "textbox_list", "rating"];

if (props.productType !== PRODUCT_TYPE.QUIZ) {
  includedTypes.push(...["single_options", "multiple_options", "radio", "checkbox"]);
}

const mapFieldOptions = (item) => ({
  // Prefer hiddenFieldQueryKey; fall back to fieldKey (custom objects) or tag
  value: item.hiddenFieldQueryKey || item.fieldKey || item.tag,
  // Prefer element label; fall back to customFieldLabel/name/fieldKey
  label:
    item.label || item.customFieldLabel || item.name || item.fieldKey || item.hiddenFieldQueryKey,
  type: item.type,
  disabled: !includedTypes.includes(item.type),
  picklistOptions: item.picklistOptions || item?.picklistOptionsImage?.map((x) => x.label) || null,
});

let fieldOptions;

const filterFunction = (i) =>
  (!["button", "header", "html", "image"].includes(i?.tag) &&
    !i?.picklistOptionsImage?.length &&
    (((i?.model === "contact" ||
      i?.model === "business" ||
      !i?.model ||
      isTrueCustomObjectField(i?.model)) &&
      includedTypes.includes(i?.type)) ||
      (["monetory", "numerical"].includes(i?.type) && i?.model === "opportunity"))) ||
  i?.type === "score";

if (store.formId) {
  fieldOptions = store.elements.filter(filterFunction).map(mapFieldOptions);
} else {
  fieldOptions = store.slides.reduce((acc, slide) => {
    const slideFieldOptions = slide.slideData.filter(filterFunction).map(mapFieldOptions);
    return acc.concat(slideFieldOptions);
  }, []);
}

const fields = ref(props?.calculation || []);

const fieldsWithLabels = computed(() =>
  fields.value.map((field) => {
    const fieldOption = fieldOptions.find((option) => option.value === field);
    return fieldOption ? fieldOption.label : field;
  })
);

// Initialize cursor position when fields change
watch(
  () => props.calculation,
  (newCalculation) => {
    fields.value = newCalculation || [];
    cursorPosition.value = fields?.value?.length;
  },
  { immediate: true }
);

// Ensure cursor position is always valid
watch(cursorPosition, (newPos) => {
  const maxPos = fields.value?.length;
  if (newPos < 0) {
    cursorPosition.value = 0;
  }
  if (newPos > maxPos) {
    cursorPosition.value = maxPos;
  }
});

const numberPad = [
  "←",
  "(",
  ")",
  "*",
  "7",
  "8",
  "9",
  "-",
  "4",
  "5",
  "6",
  "+",
  "1",
  "2",
  "3",
  "/",
  "0",
  ".",
];

const buttonStyle = (num) => {
  if (["+", "-", "*", "/"].includes(num)) {
    return { backgroundColor: "#F38744", color: "white" };
  } else if (num === "←") {
    return { backgroundColor: "#101828", color: "white" };
  }
  return { backgroundColor: "#E5E7EB", color: "#000" };
};

// Cursor management functions
const setCursorPosition = (position: number) => {
  cursorPosition.value = Math.max(0, Math.min(position, fields.value?.length));
  focusContainer();
};

const focusContainer = () => {
  nextTick(() => {
    expressionContainer.value?.focus();
  });
};

const handleContainerClick = (event: MouseEvent) => {
  const container = expressionContainer.value;
  if (!container) {
    return;
  }

  const fieldElements = container.querySelectorAll(".n-tag");
  const clickX = event.clientX;
  const clickY = event.clientY;

  if (fieldElements.length === 0) {
    setCursorPosition(0);
    focusContainer();
    return;
  }

  // Group fields by rows based on their Y position
  const rows: { elements: Element[]; y: number; indices: number[] }[] = [];

  fieldElements.forEach((element, index) => {
    const rect = element.getBoundingClientRect();
    const rowY = Math.round(rect.top);

    let row = rows.find((r) => Math.abs(r.y - rowY) < 5);
    if (!row) {
      row = { elements: [], y: rowY, indices: [] };
      rows.push(row);
    }
    row.elements.push(element);
    row.indices.push(index);
  });

  // Sort rows by Y position (top to bottom)
  rows.sort((a, b) => a.y - b.y);

  // Find which row was clicked
  let targetRow = rows[0];
  for (const row of rows) {
    const firstRect = row.elements[0].getBoundingClientRect();
    const lastRect = row.elements[row.elements.length - 1].getBoundingClientRect();

    // Check if click Y is within this row's vertical range (with some padding)
    if (clickY >= firstRect.top - 10 && clickY <= lastRect.bottom + 10) {
      targetRow = row;
      break;
    }

    // If click is below the last row, use the last row
    if (row === rows[rows.length - 1]) {
      targetRow = row;
    }
  }

  // Build positions for the target row
  const positions: { index: number; x: number }[] = [];

  targetRow.elements.forEach((element, rowIndex) => {
    const rect = element.getBoundingClientRect();
    const actualIndex = targetRow.indices[rowIndex];

    // Position before this field
    if (rowIndex === 0) {
      positions.push({ index: actualIndex, x: rect.left - 10 });
    } else {
      const prevRect = targetRow.elements[rowIndex - 1].getBoundingClientRect();
      const gapCenter = (prevRect.right + rect.left) / 2;
      positions.push({ index: actualIndex, x: gapCenter });
    }
  });

  // Position after the last field in this row
  const lastElement = targetRow.elements[targetRow.elements.length - 1];
  const lastRect = lastElement.getBoundingClientRect();
  const lastIndex = targetRow.indices[targetRow.indices.length - 1];
  positions.push({ index: lastIndex + 1, x: lastRect.right + 10 });

  // Find the closest position to the click in this row
  let bestPosition = 0;
  let minDistance = Infinity;

  positions.forEach((pos) => {
    const distance = Math.abs(clickX - pos.x);
    if (distance < minDistance) {
      minDistance = distance;
      bestPosition = pos.index;
    }
  });

  setCursorPosition(bestPosition);
  focusContainer();
};

const handleKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case "ArrowLeft":
      event.preventDefault();
      setCursorPosition(cursorPosition.value - 1);
      break;
    case "ArrowRight":
      event.preventDefault();
      setCursorPosition(cursorPosition.value + 1);
      break;
    case "Home":
      event.preventDefault();
      setCursorPosition(0);
      break;
    case "End":
      event.preventDefault();
      setCursorPosition(fields.value?.length);
      break;
    case "Backspace":
      event.preventDefault();
      if (cursorPosition.value > 0) {
        fields.value?.splice(cursorPosition.value - 1, 1);
        setCursorPosition(cursorPosition.value - 1);
        emit("update:calculation", fields.value);
      }
      break;
    case "Delete":
      event.preventDefault();
      if (cursorPosition.value < fields.value.length) {
        fields.value?.splice(cursorPosition.value, 1);
        emit("update:calculation", fields.value);
      }
      break;
    case "Escape":
      // Clear selection and reset cursor to end
      setCursorPosition(fields.value?.length);
      break;
  }
};

const addField = () => {
  if (selectedField.value) {
    insertAtCursor(selectedField.value);
    selectedField.value = null;
  }
};

const addNumber = (num: string) => {
  if (num === "←") {
    // Backspace - delete before cursor
    if (cursorPosition.value > 0) {
      fields.value?.splice(cursorPosition.value - 1, 1);
      setCursorPosition(cursorPosition.value - 1);
    }
  } else {
    // Insert at cursor position
    insertAtCursor(num);
  }
  emit("update:calculation", fields.value);
};

const insertAtCursor = (value: string) => {
  fields.value?.splice(cursorPosition.value, 0, value);
  setCursorPosition(cursorPosition.value + 1);
};

// Focus container on mount
onMounted(() => {
  focusContainer();
});
</script>

<style>
#add-field .n-base-selection-overlay__wrapper,
#add-field .n-base-suffix__arrow {
  color: #333639 !important;
}

.cursor-blink {
  animation: blink 1s infinite;
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}
</style>
